/**
 * HITOFURI 一振り案内 — UIコントローラー
 *
 * 依存: assets/hitofuri-finder-engine.js (window.HitofuriFinderEngine)
 * 設置: <div data-hf-finder> + 設定JSON (<script type="application/json" data-hf-finder-config>)
 *       または window.HITOFURI_FINDER_CONFIG (完全ローカル版)
 *
 * - グローバル汚染なし (公開シンボルは window.HitofuriFinderUI のみ)
 * - 同一ページ複数設置・テーマエディター再描画 (shopify:section:load) 対応
 * - 回答はメモリ内のみ。個人情報・Cookie・外部送信なし。
 * - 計測は CustomEvent のみ発火 (§7-4)。GA4等への実送信はアダプター未接続。
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  var Engine = window.HitofuriFinderEngine;
  if (!Engine) return;

  var EVENT_NAMES = {
    view: 'hitofuri_finder_view',
    start: 'hitofuri_finder_start',
    answer: 'hitofuri_finder_answer',
    factView: 'hitofuri_finder_fact_view',
    complete: 'hitofuri_finder_complete',
    resultView: 'hitofuri_finder_result_view',
    primaryClick: 'hitofuri_finder_primary_click',
    secondaryClick: 'hitofuri_finder_secondary_click',
    restart: 'hitofuri_finder_restart'
  };

  // デバッグパネルの結果直行ボタン用プリセット (§16)
  var DEBUG_JUMPS = {
    shiranagi: { q2: 'A', q4: 'D', q6: 'A', q7: 'A' },
    masame: { q2: 'A', q4: 'D', q6: 'A', q7: 'B' },
    shikkoku: { q2: 'A', q4: 'D', q6: 'A', q7: 'C' },
    kurogane: { q2: 'A', q4: 'D', q6: 'A', q7: 'D' },
    omamori_silver_uroko: { q2: 'B', q4: 'A', q6: 'B', q7: 'A' },
    omamori_silver_kiku: { q2: 'B', q4: 'A', q6: 'B', q7: 'B' },
    omamori_black_uroko: { q2: 'B', q4: 'A', q6: 'B', q7: 'C' },
    omamori_black_kiku: { q2: 'B', q4: 'A', q6: 'B', q7: 'D' },
    card_small: { q2: 'C', q4: 'A', q6: 'C', q7: 'A' },
    card_large: { q2: 'C', q4: 'A', q6: 'C', q7: 'B' },
    card_small_tsuba: { q2: 'C', q4: 'A', q6: 'C', q7: 'C' },
    card_large_tsuba: { q2: 'C', q4: 'A', q6: 'C', q7: 'D' }
  };

  var START_COPY = {
    label: 'HITOFURI 一振り案内',
    heading: '一振りを知り、\nあなたの一振りに出会う。',
    body: [
      '玉鋼は、どのように生まれるのか。\n日本刀の肌に、なぜ模様が現れるのか。\n守り刀は、なぜ人生の節目に添えられてきたのか。',
      "日本刀文化を知る七つの問いをたどりながら、\n暮らしに合うHITOFURIをご案内します。"
    ],
    strong: '知識問題は、学びを楽しむための問いです。',
    afterStrong: "どのようにお迎えになり、どのような意味を重ねたいとお考えかを大切にします。",
    time: 'ご希望の確認＋日本刀文化を知る7つの問い',
    selfButton: "ご自身の一振りを探す",
    giftButton: '贈る一振りを探す',
    catalogLink: 'すべての一振りを見る'
  };

  /* ------------------------------------------------------------ util */

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text; // 動的文字列は常に textContent (§18)
    return node;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function hasDebugQuery() {
    return /[?&]hf_debug=1(&|$)/.test(window.location.search);
  }

  /* ------------------------------------------------------------ 本体 */

  function Finder(container, config) {
    this.container = container;
    this.config = config || {};
    this.config.results = this.config.results || {};
    this.debug = this.config.designMode === true ||
      hasDebugQuery() ||
      (window.location.hostname === '127.0.0.1' && this.config.mode === 'local' && hasDebugQuery());
    this.state = { audience: null, answers: {}, history: [], step: 'start' };
    this.eventLog = [];
    this.forgingTimer = null;
    this.debugPanel = null;
    this.build();
    this.render();
    this.track(EVENT_NAMES.view, { mode: this.config.mode || 'unknown' });
    // デバッグ時のみ: ?hf_jump=<resultId> で結果画面へ直接移動 (スクリーンショット検証用)
    // ?hf_aud=gift を併用すると贈り物用表示で確認できる
    if (this.debug) {
      var jump = (window.location.search.match(/[?&]hf_jump=([a-z_]+)/) || [])[1];
      var aud = (window.location.search.match(/[?&]hf_aud=(self|gift)/) || [])[1];
      if (aud) this.state.audience = aud;
      if (jump && DEBUG_JUMPS[jump]) this.applyJump(jump);
    }
  }

  Finder.prototype.applyJump = function (rid) {
    var preset = DEBUG_JUMPS[rid];
    if (!preset) return;
    this.state.answers = { q1: 'B', q3: 'B', q5: 'B', q2: preset.q2, q4: preset.q4, q6: preset.q6 };
    var q7Id = Engine.resolveQ7Id(this.state.answers);
    this.state.answers[q7Id] = preset.q7;
    if (!this.state.audience) this.state.audience = 'self';
    this.state.history = [];
    this.goTo('result', { noHistory: true });
  };

  Finder.prototype.build = function () {
    var c = this.container;
    c.classList.add('hf-finder');
    while (c.firstChild) c.removeChild(c.firstChild);

    var inner = el('div', 'hf-finder__inner');

    if (this.config.mode === 'local') {
      inner.appendChild(el('p', 'hf-finder__demo-flag', 'LOCAL DEMO｜販売ページには未公開'));
    }

    this.progressWrap = el('div', 'hf-finder__progress');
    this.progressLabel = el('p', 'hf-finder__progress-label', '');
    this.progressTrack = el('div', 'hf-finder__progress-track');
    this.progressTrack.setAttribute('role', 'progressbar');
    this.progressTrack.setAttribute('aria-valuemin', '0');
    this.progressTrack.setAttribute('aria-valuemax', '7');
    this.progressTrack.setAttribute('aria-label', '一振り案内の進捗');
    this.progressFill = el('div', 'hf-finder__progress-fill');
    this.progressTrack.appendChild(this.progressFill);
    this.progressWrap.appendChild(this.progressLabel);
    this.progressWrap.appendChild(this.progressTrack);
    inner.appendChild(this.progressWrap);

    this.card = el('div', 'hf-finder__card');
    inner.appendChild(this.card);

    this.announcer = el('div', null);
    this.announcer.setAttribute('aria-live', 'polite');
    this.announcer.style.position = 'absolute';
    this.announcer.style.width = '1px';
    this.announcer.style.height = '1px';
    this.announcer.style.overflow = 'hidden';
    this.announcer.style.clipPath = 'inset(50%)';
    inner.appendChild(this.announcer);

    c.appendChild(inner);

    if (this.debug) this.buildDebugPanel();
  };

  Finder.prototype.track = function (name, detail) {
    var payload = detail || {};
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: payload }));
    } catch (e) { /* CustomEvent非対応環境では計測なしで続行 */ }
    this.eventLog.push({ name: name, detail: payload });
    if (this.eventLog.length > 60) this.eventLog.shift();
    if (this.debug && window.console && console.log) {
      console.log('[hf-finder]', name, payload);
    }
    this.updateDebugPanel();
  };

  /* ------------------------------------------------------------ 遷移 */

  Finder.prototype.goTo = function (step, opts) {
    opts = opts || {};
    if (!opts.noHistory && this.state.step !== step) {
      this.state.history.push(this.state.step);
    }
    this.state.step = step;
    this.render();
  };

  Finder.prototype.goBack = function () {
    if (this.forgingTimer) { clearTimeout(this.forgingTimer); this.forgingTimer = null; }
    var prev = this.state.history.pop();
    if (!prev) prev = 'start';
    this.state.step = prev;
    this.render();
  };

  Finder.prototype.restart = function () {
    if (this.forgingTimer) { clearTimeout(this.forgingTimer); this.forgingTimer = null; }
    this.state = { audience: null, answers: {}, history: [], step: 'start' };
    this.track(EVENT_NAMES.restart, {});
    this.render();
  };

  Finder.prototype.currentQuestionId = function (step) {
    if (step === 'q7') return Engine.resolveQ7Id(this.state.answers);
    return step;
  };

  Finder.prototype.answer = function (step, qid, value) {
    if (!Engine.isValidAnswer(qid, value)) return; // 無効値は無視 (§19-1)
    this.state.answers[qid] = value;
    var q = Engine.getQuestion(qid);
    this.track(EVENT_NAMES.answer, { questionId: qid, number: q.number, value: value });
    if (q.kind === 'knowledge') {
      this.goTo('door_' + qid);
      return;
    }
    var NEXT = { q2: 'q3', q4: 'q5', q6: 'q7' };
    if (step === 'q7') {
      this.track(EVENT_NAMES.complete, { answers: this.snapshotAnswers() });
      this.goTo('forging');
      return;
    }
    this.goTo(NEXT[qid]);
  };

  Finder.prototype.snapshotAnswers = function () {
    var out = {};
    for (var k in this.state.answers) {
      if (Object.prototype.hasOwnProperty.call(this.state.answers, k)) out[k] = this.state.answers[k];
    }
    return out;
  };

  /* ------------------------------------------------------------ 描画 */

  Finder.prototype.render = function () {
    var step = this.state.step;
    var card = this.card;
    while (card.firstChild) card.removeChild(card.firstChild);
    var screen = el('div', 'hf-finder__screen');

    var number = this.stepNumber(step);
    if (number) {
      this.progressWrap.style.display = '';
      this.progressLabel.textContent = number + '／7';
      this.progressTrack.setAttribute('aria-valuenow', String(number));
      this.progressTrack.setAttribute('aria-valuetext', '全7問中 ' + number + '問目');
      this.progressFill.style.width = (number / 7 * 100) + '%';
    } else {
      this.progressWrap.style.display = 'none';
    }

    if (step === 'start') this.renderStart(screen);
    else if (step === 'constraints') this.renderConstraints(screen);
    else if (step.indexOf('door_') === 0) this.renderDoor(screen, step.slice(5));
    else if (step === 'forging') this.renderForging(screen);
    else if (step === 'result') this.renderResult(screen);
    else this.renderQuestion(screen, step);

    card.appendChild(screen);
    this.focusHeading(screen);
    this.updateDebugPanel();
  };

  Finder.prototype.stepNumber = function (step) {
    if (step === 'q7') return 7;
    if (/^q[1-6]$/.test(step)) return parseInt(step.slice(1), 10);
    if (step.indexOf('door_') === 0) {
      var q = Engine.getQuestion(step.slice(5));
      return q ? q.number : null;
    }
    return null;
  };

  Finder.prototype.focusHeading = function (screen) {
    var heading = screen.querySelector('.hf-finder__heading');
    if (!heading) return;
    heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: false });
    var label = this.progressLabel.textContent;
    this.announcer.textContent = (label ? '問' + label + '。' : '') + heading.textContent;
  };

  Finder.prototype.addFooterActions = function (screen, opts) {
    opts = opts || {};
    var actions = el('div', 'hf-finder__actions');
    var self = this;
    if (opts.back !== false && this.state.history.length > 0) {
      var back = el('button', 'hf-finder__textlink', '前へ戻る');
      back.type = 'button';
      back.addEventListener('click', function () { self.goBack(); });
      actions.appendChild(back);
    }
    if (opts.restart !== false && this.state.step !== 'start') {
      var restart = el('button', 'hf-finder__textlink', '最初からやり直す');
      restart.type = 'button';
      restart.addEventListener('click', function () { self.restart(); });
      actions.appendChild(restart);
    }
    var catalogUrl = this.config.catalogUrl;
    if (catalogUrl) {
      var link = el('a', 'hf-finder__textlink', 'すべての一振りを見る');
      link.href = catalogUrl;
      actions.appendChild(link);
    }
    if (actions.childNodes.length) screen.appendChild(actions);
  };

  // self/gift で文言が異なる選択肢フィールドの解決
  Finder.prototype.choiceText = function (choice, key) {
    if (this.state.audience === 'gift') {
      var gift = choice[key + 'Gift'];
      if (gift) return gift;
    }
    return choice[key];
  };

  /* ---------------------------------------------------------- 各画面 */

  Finder.prototype.renderStart = function (screen) {
    var self = this;
    screen.appendChild(el('p', 'hf-finder__kicker', START_COPY.label));
    screen.appendChild(el('h2', 'hf-finder__heading', START_COPY.heading));
    START_COPY.body.forEach(function (p) {
      screen.appendChild(el('p', 'hf-finder__body', p));
    });
    var strong = el('p', 'hf-finder__emphasis', START_COPY.strong);
    screen.appendChild(strong);
    screen.appendChild(el('p', 'hf-finder__body', START_COPY.afterStrong));
    screen.appendChild(el('p', 'hf-finder__lead', START_COPY.time));

    var actions = el('div', 'hf-finder__actions');
    [['self', START_COPY.selfButton], ['gift', START_COPY.giftButton]].forEach(function (pair) {
      var btn = el('button', 'hf-finder__button', pair[1]);
      btn.type = 'button';
      btn.addEventListener('click', function () {
        self.state.audience = pair[0];
        self.track(EVENT_NAMES.start, { audience: pair[0] });
        self.goTo('constraints');
      });
      actions.appendChild(btn);
    });
    screen.appendChild(actions);

    if (this.config.catalogUrl) {
      var sub = el('div', 'hf-finder__actions');
      var link = el('a', 'hf-finder__textlink', START_COPY.catalogLink);
      link.href = this.config.catalogUrl;
      sub.appendChild(link);
      screen.appendChild(sub);
    }
  };

  Finder.prototype.renderQuestion = function (screen, step) {
    var self = this;
    var qid = this.currentQuestionId(step);
    var q = Engine.getQuestion(qid);
    if (!q) { this.restart(); return; }

    screen.appendChild(el('p', 'hf-finder__kicker', '問' + q.number + '／7｜' + q.title));
    var text = q.kind === 'knowledge'
      ? q.question
      : (this.state.audience === 'gift' ? q.questionGift : q.questionSelf);
    screen.appendChild(el('h2', 'hf-finder__heading', text));

    if (qid === 'q7_toukengata') {
      screen.appendChild(el('p', 'hf-finder__important',
        this.state.answers.requestedSeries === 'kurogane'
        ? '鐵を指定済みです。鉄製の素材と仕様をご案内します。'
        : '刀剣型では玉鋼製の白凪・柾目・漆黒を中心にご案内します。鐵を明示してお探しの方も仕様を確認できます。'));
    }
    // 最終質問 (問7) は実物画像つきの選択カード (§15)
    var isFinal = qid.indexOf('q7_') === 0;
    var choices = el('div', 'hf-finder__choices' + (isFinal ? ' hf-finder__choices--visual' : ''));
    q.choices.forEach(function (choice) {
      if (qid === 'q7_toukengata' && self.state.answers.requestedSeries === 'kurogane' && choice.value !== 'D') return;
      var btn = el('button', 'hf-finder__choice' + (isFinal ? ' hf-finder__choice--visual' : ''));
      btn.type = 'button';

      if (isFinal) {
        var rid = (Engine.CHOICE_RESULT_MAP[qid] || {})[choice.value];
        var rcfg = self.config.results[rid];
        var imgs = Engine.resolveChoiceImages(rcfg);
        var media = el('span', 'hf-finder__choice-media');
        if (imgs.length) {
          imgs.slice(0, 2).forEach(function (src, idx) {
            var im = document.createElement('img');
            im.src = src;
            im.loading = 'lazy';
            im.width = 104;
            im.height = imgs.length > 1 ? 72 : 104;
            im.alt = choice.label + 'の実物写真' +
              (imgs.length > 1 ? '（' + (idx === 0 ? '刀身' : '結び') + '）' : '');
            media.appendChild(im);
          });
        } else {
          // 正しい画像がないときは別商品画像で代用しない (§15)
          media.appendChild(el('span', 'hf-finder__choice-media-pending',
            (rcfg && rcfg.choiceImagePending) || '画像設定待ち'));
        }
        btn.appendChild(media);
      }

      var textBox = el('span', isFinal ? 'hf-finder__choice-text' : null);
      textBox.appendChild(el('span', 'hf-finder__choice-label', self.choiceText(choice, 'label')));
      var lead = self.choiceText(choice, 'lead');
      if (lead) textBox.appendChild(el('span', 'hf-finder__choice-lead', lead));
      var desc = self.choiceText(choice, 'desc');
      if (desc) textBox.appendChild(el('span', 'hf-finder__choice-desc', desc));
      btn.appendChild(textBox);

      btn.addEventListener('click', function () { self.answer(step, qid, choice.value); });
      choices.appendChild(btn);
    });
    screen.appendChild(choices);
    this.addFooterActions(screen);
  };

  Finder.prototype.renderDoor = function (screen, qid) {
    var self = this;
    var q = Engine.getQuestion(qid);
    if (!q || !q.door) { this.restart(); return; }
    var door = q.door;

    screen.appendChild(el('p', 'hf-finder__kicker', door.label));
    screen.appendChild(el('h2', 'hf-finder__heading', door.answer));
    door.body.forEach(function (p) {
      screen.appendChild(el('p', 'hf-finder__body', p));
    });
    screen.appendChild(el('p', 'hf-finder__emphasis', door.emphasis));
    if (door.after) screen.appendChild(el('p', 'hf-finder__body', door.after));

    this.track(EVENT_NAMES.factView, { questionId: qid, door: door.label });

    var NEXT = { q1: 'q2', q3: 'q4', q5: 'q6' };
    var actions = el('div', 'hf-finder__actions');
    var next = el('button', 'hf-finder__button', '次の問いへ');
    next.type = 'button';
    next.addEventListener('click', function () { self.goTo(NEXT[qid]); });
    actions.appendChild(next);
    screen.appendChild(actions);
    this.addFooterActions(screen, {});
  };

  Finder.prototype.renderForging = function (screen) {
    var self = this;
    var wrap = el('div', 'hf-finder__forging');

    // 細い金色の線が刀身の輪郭のように伸び、火の粉が一度だけ現れる (§8-5)
    var svgNS = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('viewBox', '0 0 320 60');
    svg.setAttribute('class', 'hf-finder__forging-svg');
    svg.setAttribute('aria-hidden', 'true');
    var path = document.createElementNS(svgNS, 'path');
    path.setAttribute('d', 'M8 44 Q120 20 240 26 Q280 28 306 18 L312 16 L306 24 Q280 34 240 32 Q120 28 10 48 Z');
    path.setAttribute('class', 'hf-finder__forging-line');
    svg.appendChild(path);
    [[250, 18, 1.4], [270, 12, 1.0], [288, 20, 1.2]].forEach(function (s, i) {
      var c = document.createElementNS(svgNS, 'circle');
      c.setAttribute('cx', String(s[0]));
      c.setAttribute('cy', String(s[1]));
      c.setAttribute('r', String(s[2]));
      c.setAttribute('class', 'hf-finder__forging-spark');
      c.style.animationDelay = (800 + i * 90) + 'ms';
      svg.appendChild(c);
    });
    wrap.appendChild(svg);
    wrap.appendChild(el('p', 'hf-finder__forging-text', "お選びいただいた答えを、一振りの形へ結んでいます。"));
    wrap.classList.add('is-brighten');
    screen.appendChild(wrap);

    var delay = prefersReducedMotion() ? 150 : 1500; // 1.2〜1.8秒 (§8-5)
    this.forgingTimer = window.setTimeout(function () {
      self.forgingTimer = null;
      self.goTo('result');
    }, delay);
  };

  /* ------------------------------------------------------ 結果画面 */

  Finder.prototype.resultConfig = function (resultId) {
    var c = this.config.results[resultId];
    return (c && typeof c === 'object') ? c : null;
  };

  /**
   * CTAの解決 (§14-3 / §14-4)。
   * 商品未設定 → デモ表示。売り切れ → 代替商品 → コレクション → 第二候補 → 同系統一覧。
   */
  Finder.prototype.resolveCta = function (resultId, secondaryId) {
    var c = this.resultConfig(resultId);
    var copy = Engine.RESULTS[resultId];
    if (!c || c.enabled === false || !c.url) {
      var fallback = c && c.fallbackUrl ? c.fallbackUrl : this.config.catalogUrl;
      return { mode: 'demo', url: fallback || null, label: fallback ? '同じ系統の作品一覧を見る' : null };
    }
    if (c.available !== false) {
      return { mode: 'product', url: c.url, label: Engine.resolvePrimaryCtaLabel(c) };
    }
    if (c.altUrl) {
      return { mode: 'alt', url: c.altUrl, label: '近い仕立てのご案内を見る',
        note: 'この仕立ては現在お品切れです。近いご案内をご用意しています。' };
    }
    if (c.fallbackUrl) {
      return { mode: 'collection', url: c.fallbackUrl, label: '現在ご案内できる同系統の商品を見る',
        note: 'この仕立ては現在お品切れです。' };
    }
    var sc = this.resultConfig(secondaryId);
    if (sc && sc.url && sc.available !== false) {
      return { mode: 'secondary', url: sc.url, label: '第二候補のご案内を見る',
        note: 'この仕立ては現在お品切れです。第二候補をご案内します。' };
    }
    return { mode: 'demo', url: this.config.catalogUrl || null,
      label: this.config.catalogUrl ? '現在ご案内できる同系統の商品を見る' : null,
      note: 'この仕立ては現在お品切れです。' };
  };

  /**
   * 結果画面 (§18 の順序)。
   * 1結果ラベル → 2商品名 → 3商品画像 → 4価格 → 5自然な推薦理由 → 6ベネフィット
   * → 7代表画像注記 → 8主CTA → 9コレクションCTA → 10商品説明
   * → 11あなたが大切にしたこと → 12この一振りにつながる日本刀文化
   * → 13もう一つ、近い一振り → 14すべての一振りを見る → 15もう一度、一振りを選ぶ
   */
  Finder.prototype.renderResult = function (screen) {
    var self = this;
    var result = Engine.computeResult(this.state.answers);
    if (!result.complete) {
      // 想定外の不完全状態でも壊れず最初へ戻す
      this.restart();
      return;
    }
    var isGift = this.state.audience === 'gift';
    var copy = Engine.RESULTS[result.primaryId];
    var secondCopy = Engine.RESULTS[result.secondaryId];
    var cfg = this.resultConfig(result.primaryId);
    var cta = this.resolveCta(result.primaryId, result.secondaryId);
    if (!Engine.assessBudgetSelection(this.state.constraints).valid) {
      this.state.returnToResult=true;
      this.goTo('constraints');
      return;
    }
    this.appendBudgetSummary(screen);
    var assessment = Engine.assessPurchase(cfg, this.state.constraints, this.today());
    if (!assessment.eligible) {
      this.renderNoMatch(screen, result, assessment);
      return;
    }
    screen.appendChild(el('p', 'hf-finder__note', assessment.delivery));
    screen.appendChild(el('p', 'hf-finder__note', '本体価格（税込）。送料・別売品は含まれません。'));
    screen.appendChild(el('p', 'hf-finder__note', assessment.deliveryNote));

    this.track(EVENT_NAMES.resultView, {
      primary: result.primaryId, secondary: result.secondaryId,
      category: result.category, audience: this.state.audience
    });

    // 1. 結果ラベル
    screen.appendChild(el('p', 'hf-finder__kicker', "一振りのご案内"));
    screen.appendChild(el('p', 'hf-finder__lead',
      isGift ? '大切な方へ贈る一振り' : 'いまの暮らしに合う一振り'));

    // 2. 商品名
    var displayName = (cfg && cfg.titleOverride) ? cfg.titleOverride : copy.name;
    screen.appendChild(el('h2', 'hf-finder__heading', displayName));

    // 3. 商品画像 (未設定なら別商品画像で代用せずデモ表示 §15/§14-3)
    var images = Engine.resolveResultImages(cfg);
    if (!images.length) {
      screen.appendChild(el('p', 'hf-finder__badge', '商品設定前・デモ表示'));
    }
    var imageWrap = el('div', 'hf-finder__result-image' +
      (images.length > 1 ? ' hf-finder__result-image--pair' : ''));
    if (images.length) {
      images.slice(0, 2).forEach(function (src, idx) {
        var img = document.createElement('img');
        img.src = src;
        img.loading = 'lazy';
        img.width = 800; img.height = 600;
        img.alt = ((cfg && cfg.imageAlt) || (copy.shortName + 'の商品写真')) +
          (images.length > 1 ? '（' + (idx === 0 ? '仕立て' : '刀身') + '）' : '');
        imageWrap.appendChild(img);
      });
    } else {
      imageWrap.appendChild(el('div', 'hf-finder__placeholder', 'DEMO IMAGE'));
    }
    screen.appendChild(imageWrap);

    // 4. 価格
    if (cfg && cfg.priceLabel && cta.mode === 'product') {
      screen.appendChild(el('p', 'hf-finder__price', cfg.priceLabel));
    }

    // 5. 自然な推薦理由 (§20)
    var reason = Engine.buildNaturalReason(this.state.answers, this.state.audience || 'self');
    if (reason) {
      screen.appendChild(el('p', 'hf-finder__body', reason.sentence));
      screen.appendChild(el('p', 'hf-finder__body', reason.closing));
    }

    // 6. ベネフィット (self/gift 分岐 §24-26)
    var benefit = (isGift && copy.benefitGift) ? copy.benefitGift : (copy.benefitSelf || []);
    benefit.forEach(function (p) {
      screen.appendChild(el('p', 'hf-finder__body', p));
    });

    // 7. 代表画像注記 (§23)
    var imageNote = Engine.resolveImageNote(cfg);
    if (imageNote && images.length) {
      screen.appendChild(el('p', 'hf-finder__note', imageNote));
    }

    // 8. 主CTA /* v2-order: primary-cta */
    var mainActions = el('div', 'hf-finder__actions');
    if (cta.url && cta.label) {
      var link = el('a', 'hf-finder__button', cta.label);
      link.href = cta.url;
      link.addEventListener('click', function () {
        self.track(EVENT_NAMES.primaryClick, { result: result.primaryId, mode: cta.mode, url: cta.url });
      });
      mainActions.appendChild(link);
    } else {
      var disabled = el('button', 'hf-finder__button', '商品設定前・デモ表示');
      disabled.type = 'button';
      disabled.disabled = true;
      mainActions.appendChild(disabled);
    }
    screen.appendChild(mainActions);
    if (cta.note) {
      screen.appendChild(el('p', 'hf-finder__note', cta.note));
    }

    // 9. コレクションCTA (§23。検証済みURLがある場合だけ)
    var colCta = Engine.resolveCollectionCta(cfg);
    if (colCta) {
      var colActions = el('div', 'hf-finder__actions');
      var colLink = el('a', 'hf-finder__textlink', colCta.label);
      colLink.href = colCta.url;
      colLink.addEventListener('click', function () {
        self.track(EVENT_NAMES.secondaryClick, { result: result.primaryId, mode: 'collection', url: colCta.url });
      });
      colActions.appendChild(colLink);
      screen.appendChild(colActions);
    }

    // 10. 商品の特徴・素材・工程 /* v2-order: description */
    screen.appendChild(el('p', 'hf-finder__section-title', 'この一振りについて'));
    var desc = (isGift && copy.bodyGift) ? copy.bodyGift : copy.body;
    desc.forEach(function (p) {
      screen.appendChild(el('p', 'hf-finder__body', p));
    });
    if (copy.materialNote) {
      screen.appendChild(el('p', 'hf-finder__important', copy.materialNote));
    }
    if (copy.note) {
      screen.appendChild(el('p', 'hf-finder__note', copy.note));
    }

    // 11. あなたが大切にしたこと (§21)
    screen.appendChild(el('p', 'hf-finder__section-title', "大切にされたこと"));
    var summary = el('ul', 'hf-finder__summary-list');
    summary.appendChild(el('li', null, isGift ? '贈る一振りを探す' : "ご自身の一振りを探す"));
    ['q2', 'q4', 'q6'].forEach(function (qid) {
      var q = Engine.getQuestion(qid);
      var val = self.state.answers[qid];
      for (var i = 0; i < q.choices.length; i++) {
        if (q.choices[i].value === val) {
          summary.appendChild(el('li', null, self.choiceText(q.choices[i], 'label')));
          break;
        }
      }
    });
    screen.appendChild(summary);

    // 12. この一振りにつながる日本刀文化 (§22: 結果ごとに1項目だけ)
    screen.appendChild(el('p', 'hf-finder__section-title', 'この一振りにつながる日本刀文化'));
    screen.appendChild(el('p', 'hf-finder__body', copy.recap));

    if (result.secondaryId !== result.primaryId && Engine.assessPurchase(this.resultConfig(result.secondaryId), this.state.constraints, this.today()).eligible) {
    // 13. もう一つ、近い一振り (§27)
    screen.appendChild(el('p', 'hf-finder__section-title', 'もう一つ、近い一振り'));
    var secondary = el('div', 'hf-finder__secondary');
    var secondCfg = this.resultConfig(result.secondaryId);
    var secondImages = Engine.resolveResultImages(secondCfg);
    if (secondImages.length) {
      var sImgWrap = el('div', 'hf-finder__secondary-image');
      var sImg = document.createElement('img');
      sImg.src = secondImages[0];
      sImg.loading = 'lazy';
      sImg.width = 160; sImg.height = 120;
      sImg.alt = secondCopy.shortName + 'の商品写真';
      sImgWrap.appendChild(sImg);
      secondary.appendChild(sImgWrap);
    }
    var sBody = el('div', 'hf-finder__secondary-body');
    sBody.appendChild(el('p', 'hf-finder__secondary-name', secondCopy.name));
    var compare = el('ul', 'hf-finder__compare-list');
    compare.appendChild(el('li', null, copy.compareSentence));
    compare.appendChild(el('li', null, secondCopy.compareSentence));
    sBody.appendChild(compare);
    var secondCta = this.resolveCta(result.secondaryId, result.primaryId);
    if (secondCta.url) {
      var secondLink = el('a', 'hf-finder__textlink',
        secondCta.mode === 'product' ? (secondCopy.shortName + 'を見る') : '同じ系統の作品一覧を見る');
      secondLink.href = secondCta.url;
      secondLink.addEventListener('click', function () {
        self.track(EVENT_NAMES.secondaryClick, { result: result.secondaryId, mode: secondCta.mode, url: secondCta.url });
      });
      sBody.appendChild(secondLink);
    } else {
      sBody.appendChild(el('p', 'hf-finder__badge', '商品設定前・デモ表示'));
    }
    secondary.appendChild(sBody);
    screen.appendChild(secondary);

    }
    this.appendEditActions(screen);
    // 14. すべての一振りを見る / 15. もう一度、一振りを選ぶ (§21)
    var footer = el('div', 'hf-finder__actions');
    if (this.config.catalogUrl) {
      var catalog = el('a', 'hf-finder__textlink', 'すべての一振りを見る');
      catalog.href = this.config.catalogUrl;
      footer.appendChild(catalog);
    }
    var retry = el('button', 'hf-finder__button hf-finder__button--ghost', 'もう一度、一振りを選ぶ');
    retry.type = 'button';
    retry.addEventListener('click', function () { self.restart(); });
    footer.appendChild(retry);
    screen.appendChild(footer);
  };


  Finder.prototype.today = function () {
    return new Date().toLocaleDateString('sv-SE', { timeZone:'Asia/Tokyo' });
  };
  Finder.prototype.renderConstraints = function (screen) {
    var self = this;
    var saved = this.state.constraints || {};
    screen.appendChild(el('h2','hf-finder__heading','ご希望を確かめる。'));
    screen.appendChild(el('p','hf-finder__body','刀剣型では玉鋼製の三つの表情を中心にご案内します。希望する形、ご予算、期日を先に確認できます。'));
    var form = el('form','hf-finder__conditions');
    form.noValidate = true;
    var prefix = this.container.closest('[id]') ? this.container.closest('[id]').id : 'hf-finder';
    function field(label,node,name) {
      node.id = prefix+'-'+name;
      var l=el('label','hf-finder__field',label);
      l.setAttribute('for',node.id); l.appendChild(node); form.appendChild(l); return node;
    }
    var shape=el('select'); shape.name='shape';
    [['auto','目的から選ぶ'],['toukengata','刀剣型〈玉鋼製〉'],['omamorigata','御守型'],['card','カード版'],['kurogane','鐵を指定する']].forEach(function(pair){
      var o=el('option',null,pair[1]);o.value=pair[0];shape.appendChild(o);
    });
    shape.value=saved.shape || 'auto'; field("ご希望の形",shape,'shape');
    var budgets=el('fieldset','hf-finder__budget');
    budgets.appendChild(el('legend','hf-finder__budget-question','本体のご予算を選んでください。'));
    var help=el('p','hf-finder__note','本体価格（税込）の上限です。送料・別売品は含みません。');
    help.id=prefix+'-budget-help';budgets.appendChild(help);
    var error=el('p','hf-finder__budget-error','予算の選択肢を1つ選んでください。');
    error.id=prefix+'-budget-error';error.hidden=true;error.setAttribute('role','alert');
    var choices=el('div','hf-finder__budget-options');
    var savedBudget=Engine.assessBudgetSelection(saved);
    Engine.BUDGET_CHOICES.forEach(function(choice){
      var label=el('label','hf-finder__budget-option');
      var radio=el('input','hf-finder__budget-radio');radio.type='radio';radio.name='budgetChoice';radio.value=choice.id;radio.required=true;
      radio.id=prefix+'-budget-'+choice.id;
      radio.checked=savedBudget.valid && savedBudget.id===choice.id;
      radio.setAttribute('aria-describedby',help.id+' '+error.id);
      radio.addEventListener('change',function(){error.hidden=true;});
      label.appendChild(radio);label.appendChild(el('span',null,choice.label));choices.appendChild(label);
    });
    budgets.appendChild(choices);budgets.appendChild(error);form.appendChild(budgets);
    var date=el('input');date.type='date';date.name='deadline';date.value=saved.deadline || '';
    field('受け取り希望日（任意）',date,'deadline');
    form.appendChild(el('p','hf-finder__note','全系統：受注生産｜2〜4週間。ご希望日まで4週間未満の場合、到着を確約できる候補として表示せず、ご相談をご案内します。'));
    var submit=el('button','hf-finder__button','この条件で質問へ進む');submit.type='submit';form.appendChild(submit);
    form.addEventListener('submit',function(event){
      event.preventDefault();
      var selected=form.querySelector('input[name="budgetChoice"]:checked');
      var budget=Engine.resolveBudgetChoice(selected && selected.value);
      if (!budget.valid) {
        error.hidden=false;
        budgets.querySelector('input').focus();
        return;
      }
      if(!form.reportValidity())return;
      self.state.constraints={shape:shape.value,budgetChoice:budget.id,deadline:date.value};
      self.state.answers.requestedCategory=shape.value==='kurogane'?'toukengata':shape.value;
      self.state.answers.requestedSeries=shape.value==='kurogane'?'kurogane':null;
      if(self.state.returnToResult) {
        self.state.returnToResult=false;
        self.goTo(Engine.computeResult(self.state.answers).complete?'result':'q7');
      } else self.goTo('q1');
    });
    screen.appendChild(form);
    var back=el('button','hf-finder__back','一つ戻る');back.type='button';back.addEventListener('click',function(){self.goBack();});screen.appendChild(back);
  };
  Finder.prototype.appendBudgetSummary = function(screen) {
    var budget=Engine.assessBudgetSelection(this.state.constraints);
    if (!budget.valid) return;
    var text='選択したご予算：'+budget.label;
    if (budget.ceiling !== null) text+='｜上限'+budget.ceiling.toLocaleString('ja-JP')+'円（税込）';
    screen.appendChild(el('p','hf-finder__budget-summary',text));
  };
  Finder.prototype.appendEditActions = function(screen) {
    var self=this, actions=el('div','hf-finder__actions');
    [['条件を変更する',function(){self.state.returnToResult=true;self.goTo('constraints');}],
     ['回答を変更する',function(){self.state.history=[];self.goTo('q2');}],
     ['一つ戻る',function(){self.goTo('q7');}]].forEach(function(pair){
       var b=el('button','hf-finder__button hf-finder__button--ghost',pair[0]);b.type='button';b.addEventListener('click',pair[1]);actions.appendChild(b);
     });
    screen.appendChild(actions);
  };
  Finder.prototype.renderNoMatch = function(screen,result,assessment) {
    var self=this, secondary=this.resultConfig(result.secondaryId);
    var other=Engine.assessPurchase(secondary,this.state.constraints,this.today());
    screen.appendChild(el('p','hf-finder__kicker','ご希望の条件と販売情報'));
    screen.appendChild(el('h2','hf-finder__heading',
      other.eligible ? '選んだ仕立ての販売条件をご確認ください。' : '選んだ表情・仕立てでは、条件に合う購入候補がありません。'));
    assessment.reasons.forEach(function(reason){screen.appendChild(el('p','hf-finder__important',reason));});
    screen.appendChild(el('p','hf-finder__note',assessment.delivery+'。'+assessment.deliveryNote));
    screen.appendChild(el('p','hf-finder__body','選んだ表情・仕立ての参考情報と、現在の条件を分けてご案内します。'));
    function summary(id, eligible) {
      var cfg=self.resultConfig(id), copy=Engine.RESULTS[id];
      if(!cfg || !copy)return;
      var box=el('div','hf-finder__secondary');
      box.appendChild(el('h3','hf-finder__secondary-name',eligible?'条件を確認できた別の候補':'選んだ表情・仕立ての参考情報'));
      box.appendChild(el('p','hf-finder__body',cfg.titleOverride || copy.name));
      var imgs=Engine.resolveResultImages(cfg);
      if(imgs.length){var im=el('img');im.src=imgs[0];im.alt=cfg.imageAlt || copy.shortName;im.width=320;im.height=240;box.appendChild(im);}
      if(cfg.priceLabel)box.appendChild(el('p','hf-finder__price',cfg.priceLabel+'（本体・税込）'));
      box.appendChild(el('p','hf-finder__body',copy.materialNote || (copy.body||[]).join(' ')));
      if(eligible)box.appendChild(el('p','hf-finder__body',copy.compareSentence));
      if(cfg.url){var a=el('a','hf-finder__textlink',eligible?'別候補の商品を見る':'商品情報を確認する');a.href=cfg.url;box.appendChild(a);}
      if(cfg.collectionUrl){var c=el('a','hf-finder__textlink','同じシリーズの色・仕立てを確認する');c.href=cfg.collectionUrl;box.appendChild(c);}
      screen.appendChild(box);
    }
    summary(result.primaryId,false);
    if(other.eligible)summary(result.secondaryId,true);
    else other.reasons.forEach(function(reason){if(assessment.reasons.indexOf(reason)===-1)screen.appendChild(el('p','hf-finder__note','比較対象の「'+(secondary.titleOverride || Engine.RESULTS[result.secondaryId].name)+'」：'+reason));});
    var support=el('a','hf-finder__textlink','期日・条件について相談する');support.href='/pages/contact';screen.appendChild(support);
    this.appendEditActions(screen);
    var retry=el('button','hf-finder__button','もう一度、一振りを選ぶ');retry.type='button';retry.addEventListener('click',function(){self.restart();});screen.appendChild(retry);
  };

  /* ------------------------------------------------------ デバッグ */

  Finder.prototype.buildDebugPanel = function () {
    var self = this;
    var panel = document.createElement('details');
    panel.className = 'hf-finder__debug';
    var summary = el('summary', null, 'HF DEBUG');
    panel.appendChild(summary);
    var body = el('div', 'hf-finder__debug-body');

    var stateTitle = el('h3', null, '状態 / スコア');
    this.debugState = el('pre', null, '');
    body.appendChild(stateTitle);
    body.appendChild(this.debugState);

    body.appendChild(el('h3', null, '結果へ直接移動'));
    var jump = el('div', 'hf-finder__debug-jump');
    Engine.RESULT_IDS.forEach(function (rid) {
      var b = el('button', null, rid);
      b.type = 'button';
      b.addEventListener('click', function () { self.applyJump(rid); });
      jump.appendChild(b);
    });
    var reset = el('button', null, '全回答リセット');
    reset.type = 'button';
    reset.addEventListener('click', function () { self.restart(); });
    jump.appendChild(reset);
    body.appendChild(jump);

    body.appendChild(el('h3', null, 'イベント履歴'));
    this.debugEvents = el('pre', null, '');
    body.appendChild(this.debugEvents);

    panel.appendChild(body);
    this.debugPanel = panel;
    this.container.appendChild(panel);
  };

  Finder.prototype.updateDebugPanel = function () {
    if (!this.debugPanel) return;
    var result = Engine.computeResult(this.state.answers);
    var scores = Engine.computeScores(this.state.answers);
    var info = {
      step: this.state.step,
      audience: this.state.audience,
      answers: this.state.answers,
      categoryScores: scores.category,
      seriesScores: scores.series,
      primary: result.complete ? result.primaryId : '(未確定)',
      secondary: result.complete ? result.secondaryId : '(未確定)'
    };
    this.debugState.textContent = JSON.stringify(info, null, 1);
    this.debugEvents.textContent = this.eventLog.slice(-12).map(function (e) {
      return e.name + ' ' + JSON.stringify(e.detail);
    }).join('\n');
  };

  Finder.prototype.destroy = function () {
    if (this.forgingTimer) { clearTimeout(this.forgingTimer); this.forgingTimer = null; }
    if (this.debugPanel && this.debugPanel.parentNode) {
      this.debugPanel.parentNode.removeChild(this.debugPanel);
    }
    while (this.container.firstChild) this.container.removeChild(this.container.firstChild);
    this.container.classList.remove('hf-finder');
    delete this.container.__hfFinder;
  };

  /* ------------------------------------------------------------ 初期化 */

  function readConfig(container) {
    var script = container.querySelector('script[type="application/json"][data-hf-finder-config]');
    if (script) {
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        if (window.console && console.warn) console.warn('[hf-finder] 設定JSONの読み取りに失敗しました');
      }
    }
    if (window.HITOFURI_FINDER_CONFIG && typeof window.HITOFURI_FINDER_CONFIG === 'object') {
      return window.HITOFURI_FINDER_CONFIG;
    }
    return {};
  }

  function initContainer(container) {
    if (container.__hfFinder) return container.__hfFinder; // 二重初期化防止 (§18)
    var finder = new Finder(container, readConfig(container));
    container.__hfFinder = finder;
    return finder;
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var containers = scope.querySelectorAll('[data-hf-finder]');
    for (var i = 0; i < containers.length; i++) initContainer(containers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Shopify テーマエディター対応 (§18)
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var containers = event.target.querySelectorAll('[data-hf-finder]');
    for (var i = 0; i < containers.length; i++) {
      if (containers[i].__hfFinder) containers[i].__hfFinder.destroy();
    }
  });

  window.HitofuriFinderUI = { init: initAll };
})();
